import { ExternalLink } from 'lucide-react';
import { BRAND_GRADIENT } from '@shared/styles/brand';
import { detectBrokerAction, type BrokerActionKind } from '../lib/brokerActionLink';

/**
 * The CQG connect dialog's logon-error line.
 *
 * Default behaviour is UNCHANGED: the broker's verbatim text, in the caller's own styling. The one
 * exception is a rejection that carries a link to CQG's OWN domain (the allowlist in
 * `../lib/brokerActionLink`): that used to be a dead end — a plain-text URL the trader could not
 * click and nothing telling them it was a link — so we reframe it in our own words with a real
 * button, while keeping CQG's original message one click away for support.
 *
 * Scope: the CQG connector dialog (owner decision, 2026-09-15) and the Add-account dialog's CQG branch
 * (owner decision, 2026-09-23 — prod 2026-09-18 a new trader hit the EULA refusal there as dead text).
 * The shared `ErrorMessage` component and the Rithmic surfaces are untouched.
 */

interface Props {
  /** The broker's verbatim `lastError` string (`CQG logon rejected (code 101): …`). */
  message: string;
  /**
   * Which of the dialog's two error slots this is, so the RAW fallback keeps the exact styling that
   * slot has always had: `form` = the red line under the credentials form, `takeover` = the small
   * subdued line under the concurrent-session prompt.
   */
  variant: 'form' | 'takeover';
}

/** Headline / body / button copy per detected kind. Honest: we never claim to accept anything. */
const COPY: Record<BrokerActionKind, { heading: string; body: string; cta: string }> = {
  agreement: {
    heading: 'CQG needs you to accept their agreement',
    body: 'Before your account can connect, CQG requires you to accept their End User License '
      + "Agreement. It opens on CQG's own site in a new tab. Once you have accepted it, come back "
      + 'and press Connect again.',
    cta: "Open CQG's agreement",
  },
  unknown: {
    heading: 'CQG needs you to finish something on their site',
    body: 'CQG refused the connection and pointed to a page on their own site. It opens in a new '
      + 'tab. See what they are asking for there, then come back and press Connect again. Their '
      + 'exact message is below.',
    cta: "Open CQG's page",
  },
};

export function CqgLogonErrorNotice({ message, variant }: Props) {
  const action = detectBrokerAction(message);

  // No allowlisted broker link ⇒ behave EXACTLY as before this component existed. Never regress the
  // generic case (a wrong password, a gateway error) into a frame that implies an action exists.
  if (!action) {
    return variant === 'form'
      ? <div className="text-[13px]" style={{ color: 'var(--loss)' }}>{message}</div>
      : <p className="text-[12px] leading-4 text-text-subtle">{message}</p>;
  }

  const copy = COPY[action.kind];
  return (
    // w-full + text-left: TakeOverView's column is `items-center`/`text-center`, and this frame is a
    // left-aligned, full-width call to action in BOTH slots.
    <div
      className="flex w-full flex-col gap-2 rounded-xl p-3 text-left"
      style={{ background: 'var(--loss-soft)', border: '1px solid var(--canvas-divider)' }}
    >
      <p className="text-[13px] font-semibold leading-5" style={{ color: 'var(--loss)' }}>
        {copy.heading}
      </p>
      <p className="text-[13px] leading-5 text-text-muted">{copy.body}</p>
      {/* rel="noopener noreferrer" so the opened page can never reach back through window.opener
          (house style: see ConsentBanner / Footer). The external-link glyph makes it visible that
          this leaves the app. */}
      <a
        href={action.url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex w-fit items-center gap-1.5 rounded-lg px-3 py-2 text-[13px] font-semibold text-white transition-opacity hover:opacity-90"
        style={{ background: BRAND_GRADIENT }}
      >
        {copy.cta}
        <ExternalLink className="h-4 w-4" aria-hidden />
      </a>
      {/* Support needs the code and CQG's exact wording to diagnose — never drop it, just fold it. */}
      <details className="rounded-lg px-2 py-1" style={{ background: 'var(--canvas-inset)' }}>
        <summary className="cursor-pointer text-[12px] text-text-muted">
          Show CQG&apos;s original message
        </summary>
        <p className="mt-1 break-words text-[12px] leading-4" style={{ color: 'var(--text-subtle)' }}>
          {message}
        </p>
      </details>
    </div>
  );
}
